import React from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, PictureAsPdf } from '@mui/icons-material';
import { EMPLOYMENT_TYPES, FORM_REFS } from '../../constants/certificationAuthorization';
import GetsBrandHeader from './GetsBrandHeader';

const emptyScopeRow = () => ({
  aircraftEngineComponentTypes: '',
  authCode: '',
  authLimitation: '',
  licenceLimitation: '',
  scopeOfAuthorization: ''
});

export const emptyGetsCm001Certificate = (overrides = {}) => ({
  dgcaApprovalNo: 'IND.145.D00164',
  dgcaApprovalDated: '2026-10-13',
  dgcaApprovalValidity: '2031-10-12',
  authorizationNo: 'GETS/CA/',
  caRevisionNo: '00',
  caRevisionDate: '',
  dateOfInitialCaIssue: '',
  validFrom: '',
  validUntil: '',
  category: '',
  dateOfBirth: '',
  licenseNumber: '',
  licenseExpiry: '',
  recurrentTrainingValidTill: '',
  scopeRows: [emptyScopeRow(), emptyScopeRow(), emptyScopeRow()],
  issued: true,
  ...overrides
});

const TERMS = [
  'The Holder will continue to be in employment/ contract with GETS-MRO & the holder is Medically fit.',
  'The AME License /DGCA Authorization/Approval/Certificate of Competency held by the Holder are current.',
  'The Holder must ensure that his /her refresher training & RT license (as applicable) is valid.',
  'The authorization number and date must be quoted while certifying the work.',
  'The authorization is valid for the period as specified and can be withdrawn at any time if deemed necessary by the Compliance Monitoring Manager.',
  'Previously issued authorization is here by invalidated.',
  'The holder shall send the signed copy of his certification Authorization within 03 days to Compliance Monitoring department from the date of issue.'
];

const ACKNOWLEDGEMENTS = [
  'I have read and understood the above-mentioned terms of authorization, company policy/procedure & provision of GETS-MRO, related to the compliance and issuance of this authorization.',
  'I am fully aware of CAR-145, approved MOE of GETS-MRO and its procedures and I acknowledge that I am entitled to carry out & certify the task within the scope of authorization.'
];

const AUTH_CODES = [
  [
    'Cat A',
    'PERMIT THE HOLDER TO ISSUE CRS AFTER MINOR SCHEDULED LINE MAINTENANCE AND SIMPLE DEFECT RECTIFICATION WITHIN THE LIMITS OF MAINTENANCE TASKS SPECIFICALLY ENDORSED ON THIS AUTHORISATION.'
  ],
  [
    'Cat B1',
    'PERMIT THE HOLDER TO ISSUE CRS FOLLOWING LINE MAINTENANCE ON AIRCRAFT STRUCTURE, POWER PLANT, MECHANICAL AND ELECTRICAL SYSTEMS, WORK ON AVIONICS SYSTEM REQUIRING SIMPLE TESTS TO PROVE THEIR SERVICEABILITY AND NOT REQUIRING TROUBLE SHOOTING AS PER THE SCOPE OF ORGANISATION APPROVAL.'
  ],
  [
    'Cat B2',
    'PERMIT THE HOLDER TO ISSUE CRS FOLLOWING LINE MAINTENANCE ON AVIONIC AND ELECTRICAL SYSTEMS, AVIONICS AND ELECTRICAL SYSTEM WITHIN ENGINE AND MECHANICAL SYSTEMS REQUIRING ONLY SIMPLE TESTS TO PROVE THEIR SERVICEABILITY AS PER THE SCOPE OF ORGANISATION APPROVAL.'
  ],
  [
    'Cat C',
    'PERMIT THE HOLDER TO ISSUE CRS FOLLOWING LINE MAINTENANCE ON AVIONIC AND ELECTRICAL SYSTEMS, AVIONICS AND ELECTRICAL SYSTEM WITHIN ENGINE AND MECHANICAL SYSTEMS REQUIRING ONLY SIMPLE TESTS TO PROVE THEIR SERVICEABILITY AS PER THE SCOPE OF ORGANISATION APPROVAL.'
  ]
];

const SPECIFIC_CODES = [
  ['P1', 'PRE-FLIGHT / TRANSIT INSPECTION / BEFORE FIRST FLIGHT INSPECTION'],
  ['P2', 'POST FLIGHT INSPECTION'],
  ['TB', 'SPECIFIC TASK BASED CERTIFICATION (as per MOE 3.9)'],
  ['AWS', 'WEIGHING & COMPUTATION / COMPUTATION OF WEIGHT SCHEDULE'],
  ['SR', 'STRUCTURAL REPAIR & CERTIFICATION'],
  ['CS', 'COMPASS SWING & CERTIFICATION'],
  ['BORE', 'BORESCOPE INSPECTION & CERTIFICATION']
];

const LIMITATIONS = [
  ['L1', 'EXCLUDING AIRFRAME DEPENDING UPON CATEGORY/SUB-CATEGORY OF LICENCE'],
  ['L2', 'EXCLUDING ENGINE DEPENDING UPON CATEGORY/SUB-CATEGORY OF LICENCE'],
  ['L3', 'EXCLUDING ELECTRICAL POWER GENERATION & DISTRIBUTION SYSTEMS.'],
  [
    'L4',
    'EXCLUDING INSTRUMENT SYSTEMS, INS/IRS AND FLIGHT DIRECTORS SYSTEMS, AUTOPILOT SYSTEMS ON AEROPLANES/ HELICOPTERS, AUTOMATIC LANDING AND AUTO THROTTLE SYSTEMS ON AEROPLANES'
  ],
  ['L5', 'EXCLUDING RADIO COMMUNICATION/NAVIGATION AND RADAR SYSTEMS.'],
  ['L6', 'EXCLUDING ELECTRICAL POWER GENERATION & DISTRIBUTION SYSTEMS ON AIRCRAFT ABOVE 5700 KGS'],
  ['L7', 'EXCLUDING AVIONIC LINE REPLACEABLE UNITS'],
  ['L8', 'EXCLUDING AVIONIC LINE REPLACEABLE UNITS ON AIRCRAFT ABOVE 5700 KGS']
];

const sectionSx = {
  bgcolor: '#f0d9a8',
  border: '1px solid',
  borderColor: 'text.primary',
  px: 1.5,
  py: 0.75,
  fontWeight: 700,
  textAlign: 'center',
  textTransform: 'uppercase',
  fontSize: '0.85rem',
  letterSpacing: 0.3
};

/**
 * GETS/CM/001 — Certification Authorization (aligned to official Issue 01 Rev 00 Jul 2026).
 */
function GetsCm001CertificateForm({
  application,
  value,
  onChange,
  readOnly = false,
  onIssue,
  onDownloadPdf,
  saving = false
}) {
  const cert = value || emptyGetsCm001Certificate();
  const scopeRows =
    Array.isArray(cert.scopeRows) && cert.scopeRows.length ? cert.scopeRows : [emptyScopeRow()];

  const set = (patch) => onChange({ ...cert, ...patch });
  const updateScope = (index, patch) => {
    const next = scopeRows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ scopeRows: next });
  };

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2.5 }, borderColor: 'text.primary', borderWidth: 2, borderStyle: 'double' }}
    >
      <GetsBrandHeader
        title="Certification Authorization"
        subtitle={`Form: ${FORM_REFS.certificate} | Issue: 01 Rev 00 Dtd Jul 2026`}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {onDownloadPdf && (
          <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
            Download {FORM_REFS.certificate} PDF
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="contained" disabled={saving} onClick={() => onIssue(true)}>
            Issue authorization
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="outlined" color="error" disabled={saving} onClick={() => onIssue(false)}>
            Do not issue
          </Button>
        )}
      </Stack>

      {/* DGCA bar */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="DGCA Approval No"
            value={cert.dgcaApprovalNo || ''}
            onChange={(e) => set({ dgcaApprovalNo: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Dated"
            InputLabelProps={{ shrink: true }}
            value={dateVal(cert.dgcaApprovalDated)}
            onChange={(e) => set({ dgcaApprovalDated: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Validity"
            InputLabelProps={{ shrink: true }}
            value={dateVal(cert.dgcaApprovalValidity)}
            onChange={(e) => set({ dgcaApprovalValidity: e.target.value })}
            {...fieldProps}
          />
        </Grid>
      </Grid>

      {/* Personal Details | Date & Validity */}
      <Grid container spacing={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Personal Details
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField fullWidth size="small" label="Name" value={application?.applicantName || ''} disabled />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Birth"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dateOfBirth)}
              onChange={(e) => set({ dateOfBirth: e.target.value })}
              {...fieldProps}
            />
            <TextField
              select={!readOnly}
              fullWidth
              size="small"
              label="Employment Type"
              value={application?.employeeType || ''}
              disabled
            >
              {EMPLOYMENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" label="Employment No" value={application?.employeeId || ''} disabled />
            <TextField
              fullWidth
              size="small"
              required
              label="Authorisation No"
              value={cert.authorizationNo || ''}
              onChange={(e) => set({ authorizationNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="CA Revision No."
              value={cert.caRevisionNo || ''}
              onChange={(e) => set({ caRevisionNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="CA Revision Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.caRevisionDate)}
              onChange={(e) => set({ caRevisionDate: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Date &amp; Validity
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="LICENCE / BAMEL / BAMEEC No"
              value={cert.licenseNumber || ''}
              onChange={(e) => set({ licenseNumber: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="Category"
              value={cert.category || ''}
              onChange={(e) => set({ category: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="License Expiry"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.licenseExpiry)}
              onChange={(e) => set({ licenseExpiry: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Initial CA issue"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dateOfInitialCaIssue)}
              onChange={(e) => set({ dateOfInitialCaIssue: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Recurrent Training Valid Till"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.recurrentTrainingValidTill)}
              onChange={(e) => set({ recurrentTrainingValidTill: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Authorization Valid From"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validFrom)}
              onChange={(e) => set({ validFrom: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Authorization Expire on"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validUntil)}
              onChange={(e) => set({ validUntil: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
      </Grid>

      {/* Details of Authorization */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Details of Authorization
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set({ scopeRows: [...scopeRows, emptyScopeRow()] })}
          >
            Add row
          </Button>
        )}
      </Stack>
      <Table size="small" sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f0d9a8' }}>
            <TableCell width={40}>Sl.</TableCell>
            <TableCell>Aircraft / Engine / Component Types</TableCell>
            <TableCell width={90}>Auth. Code</TableCell>
            <TableCell>Auth. Limitation</TableCell>
            <TableCell>Licence Limitation</TableCell>
            <TableCell>Scope of Authorization</TableCell>
            {!readOnly && <TableCell width={40} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {scopeRows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.aircraftEngineComponentTypes || ''}
                  onChange={(e) => updateScope(index, { aircraftEngineComponentTypes: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.authCode || ''}
                  onChange={(e) => updateScope(index, { authCode: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.authLimitation || ''}
                  onChange={(e) => updateScope(index, { authLimitation: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.licenceLimitation || ''}
                  onChange={(e) => updateScope(index, { licenceLimitation: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.scopeOfAuthorization || ''}
                  onChange={(e) => updateScope(index, { scopeOfAuthorization: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              {!readOnly && (
                <TableCell>
                  <IconButton
                    size="small"
                    disabled={scopeRows.length <= 1}
                    onClick={() => set({ scopeRows: scopeRows.filter((_, i) => i !== index) })}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      {/* Page 2 content — full official wording */}
      <Box sx={{ ...sectionSx, mb: 1 }}>Terms of Authorization</Box>
      <Box component="ol" sx={{ pl: 2.5, m: 0, typography: 'body2' }}>
        {TERMS.map((t) => (
          <li key={t.slice(0, 32)} style={{ marginBottom: 4 }}>
            {t}
          </li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 1, mb: 2, fontStyle: 'italic', fontWeight: 600 }}>
        Note: It is the responsibility of the holder to ensure validity of this authorization during certification of
        maintenance task and capability/limitations of authorization also.
      </Typography>

      <Grid container spacing={1} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700}>
            Compliance Monitoring Manager
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            SIGNATURE
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            STAMP
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ ...sectionSx, mb: 1 }}>Acknowledgement</Box>
      <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 2, typography: 'body2' }}>
        {ACKNOWLEDGEMENTS.map((t) => (
          <li key={t.slice(0, 32)} style={{ marginBottom: 4 }}>
            {t}
          </li>
        ))}
      </Box>

      <Grid container spacing={1} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700}>
            Holder Signature
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            SIGNATURE
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            STAMP
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ ...sectionSx, mb: 1 }}>Authorization Codes</Box>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          {AUTH_CODES.map(([code, text]) => (
            <TableRow key={code}>
              <TableCell width={72} sx={{ fontWeight: 700, verticalAlign: 'top' }}>
                {code}
              </TableCell>
              <TableCell sx={{ typography: 'caption' }}>{text}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ ...sectionSx, mb: 1 }}>Specific Authorisation Codes</Box>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          {SPECIFIC_CODES.map(([code, text]) => (
            <TableRow key={code}>
              <TableCell width={56} sx={{ fontWeight: 700 }}>
                {code}
              </TableCell>
              <TableCell sx={{ typography: 'caption' }}>{text}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ ...sectionSx, mb: 1, textTransform: 'none', fontSize: '0.75rem' }}>
        Limitations as per CAR 66 AME Licence (refer latest revision of AAC 04 of 2017)
      </Box>
      <Table size="small">
        <TableBody>
          {LIMITATIONS.map(([code, text]) => (
            <TableRow key={code}>
              <TableCell width={40} sx={{ fontWeight: 700 }}>
                {code}
              </TableCell>
              <TableCell sx={{ typography: 'caption' }}>{text}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default GetsCm001CertificateForm;
